/**
 * SessionsForum.tsx
 *
 * Purpose: Container component for the Mythbound™ session forum feature.
 * Created: 2026-03-11
 * Author: ralph-manager (Sonnet code loop)
 *
 * WHAT: Manages forum state (thread list, selected thread) and coordinates API calls.
 * WHY: Single source of truth for forum data; renders ThreadList or ThreadDetail based
 *      on user selection; handles loading and error states.
 * HOW: useParams extracts sessionId from the route. useEffect fetches threads on mount.
 *      useState manages selection and UI states. API base resolved from environment util.
 * WHERE: Rendered at /session/:id/forum — see App.tsx for route registration.
 *
 * API ENDPOINTS USED:
 *   GET  /api/forums/sessions/{sessionId}/threads   — list threads
 *   POST /api/forums/threads                        — create thread
 */

import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router';
import { Box, Heading, Spinner, Center, Text } from '@chakra-ui/react';
import { getApiBaseUrl } from '../../utils/environment';
import { ThreadList } from './ThreadList';
import { ThreadDetail, type ThreadData } from './ThreadDetail';
import type { ThreadFormPayload } from './ThreadForm';

// =============================================================================
// COMPONENT
// =============================================================================

/**
 * SessionsForum Component
 *
 * WHO: Authenticated session participants.
 * WHAT: Top-level forum page — fetches threads, manages selection, renders child views.
 * WHERE: Route /session/:id/forum (ProtectedRoute in App.tsx).
 * WHY: Centralise all forum state and API orchestration in one container.
 *
 * ASYNC PATTERN: fetchThreads is defined inside useEffect and called immediately.
 * All state transitions are guarded against component unmount via cleanup.
 */
const SessionsForum: React.FC = () => {
  // Route param — matches /session/:id from App.tsx
  const { id: sessionId } = useParams<{ id: string }>();

  const [threads, setThreads] = useState<ThreadData[]>([]);
  const [selectedThread, setSelectedThread] = useState<ThreadData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const apiBase = getApiBaseUrl();

  /** Fetch all threads for this session from the backend. */
  useEffect(() => {
    if (!sessionId) return;

    let cancelled = false;

    async function fetchThreads() {
      setLoading(true);
      setError(null);
      try {
        const res = await fetch(`${apiBase}/api/forums/sessions/${sessionId}/threads`);
        if (!res.ok) {
          if (!cancelled) {
            setError(`Failed to load threads (${res.status}). Please try again.`);
          }
          return;
        }
        const data: ThreadData[] = await res.json();
        if (!cancelled) {
          setThreads(data);
        }
      } catch {
        if (!cancelled) {
          setError('Failed to load forum. Check your connection and try again.');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    fetchThreads();

    // Cleanup: prevent stale state updates if component unmounts mid-fetch
    return () => {
      cancelled = true;
    };
  }, [sessionId, apiBase]);

  /**
   * Create a new thread via POST /api/forums/threads.
   * Returns the created ThreadData so ThreadList can optimistically update.
   * Throws on failure — ThreadForm surfaces the error.
   */
  async function handleNewThreadSubmit(payload: ThreadFormPayload): Promise<ThreadData> {
    const res = await fetch(`${apiBase}/api/forums/threads`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        session_id: sessionId,
        title: payload.title,
        content: payload.content,
      }),
    });
    if (!res.ok) {
      throw new Error(`Failed to create thread (${res.status}). Please try again.`);
    }
    const created: ThreadData = await res.json();
    return created;
  }

  /** Prepend the newly created thread and deselect any open thread. */
  function handleThreadCreated(thread: ThreadData) {
    setThreads((prev) => [thread, ...prev]);
    setSelectedThread(null);
  }

  // Guard: session ID missing from URL
  if (!sessionId) {
    return (
      <Box p={6}>
        <Text color="#e57373" fontFamily="'Montserrat', Arial, sans-serif">
          Session not found. Please navigate to a valid session.
        </Text>
      </Box>
    );
  }

  return (
    <Box
      bg="#111116"
      minH="100vh"
      p={{ base: 4, md: 8 }}
    >
      {/* Page heading */}
      <Heading
        as="h1"
        size="lg"
        color="#e0e0e0"
        fontFamily="'Cinzel', 'Times New Roman', serif"
        mb={6}
        borderBottom="1px solid #2a2a35"
        pb={3}
      >
        Session Forum
      </Heading>

      {loading ? (
        <Center py={12}>
          <Spinner color="#35889A" size="lg" />
        </Center>
      ) : error ? (
        <Box py={6}>
          <Text color="#e57373" fontSize="sm" fontFamily="'Montserrat', Arial, sans-serif">
            {error}
          </Text>
        </Box>
      ) : selectedThread ? (
        /* Thread detail view */
        <ThreadDetail
          thread={selectedThread}
          sessionId={sessionId}
          onBack={() => setSelectedThread(null)}
          onReplyPosted={() => {
            // TODO(afp-sessions-forum): refresh thread post count if displayed in list
          }}
        />
      ) : (
        /* Thread list view */
        <ThreadList
          threads={threads}
          selectedThread={selectedThread}
          onSelectThread={setSelectedThread}
          onThreadCreated={handleThreadCreated}
          onNewThreadSubmit={handleNewThreadSubmit}
        />
      )}
    </Box>
  );
};

export { SessionsForum };
